import React, { useState } from 'react';
import { Collapse, Table, Card, Tag, Button, Drawer } from 'antd';
import { motion } from 'framer-motion';
import { Crown, Zap, Rocket, Star, Info } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import PricingCalculator from './PricingCalculator';
import QuoteModal from './QuoteModal';

const PricingTable = () => {
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [quoteHideArea, setQuoteHideArea] = useState(false);
  const [discountInfoOpen, setDiscountInfoOpen] = useState(false);
  const { t } = useTranslation();

  const basePlatformColumns = [
    { 
      title: <span className="font-bold text-lg">{t('pricingTable.plan')}</span>, 
      dataIndex: 'plan', 
      key: 'plan',
      render: (text, record) => (
        <div className="flex items-center space-x-3">
          {record.icon}
          <span className="font-semibold">{text}</span>
        </div>
      )
    },
    { title: <span className="font-bold text-lg">{t('pricingTable.area')}</span>, dataIndex: 'area', key: 'area' },
    { 
      title: <span className="font-bold text-lg">{t('pricingTable.pricePerHa')}</span>, 
      dataIndex: 'price', 
      key: 'price',
      render: (price) => <span className="text-2xl font-bold text-primary">{price}</span>
    },
    { title: <span className="font-bold text-lg">{t('pricingTable.features')}</span>, dataIndex: 'features', key: 'features' }
  ];

  const basePlatformData = [
    { 
      key: '1', 
      plan: 'Sowing (Free)', 
      area: '≤ 150*', 
      price: '0 €', 
      features: 'All basic indices + weather',
      icon: <Star size={20} className="text-yellow-500" />
    },
    { 
      key: '2', 
      plan: 'Planting', 
      area: '< 1,000', 
      price: '3 €', 
      features: '—',
      icon: <Zap size={20} className="text-green-500" />
    },
    { 
      key: '3', 
      plan: 'Sprouting', 
      area: '1,000‑4,999', 
      price: '2.5 €', 
      features: '—',
      icon: <Rocket size={20} className="text-blue-500" />
    },
    { 
      key: '4', 
      plan: 'Maturation', 
      area: '5,000‑19,999', 
      price: '2 €', 
      features: '—',
      icon: <Crown size={20} className="text-purple-500" />
    },
    { 
      key: '5', 
      plan: 'Harvest', 
      area: '≥ 20,000', 
      price: '1.5 € (~)', 
      features: 'Custom quote',
      icon: <Crown size={20} className="text-gold-500" />
    }
  ];

  const addOnColumns = [
    { title: <span className="font-bold text-lg">Area bracket</span>, dataIndex: 'area', key: 'area' },
    { 
      title: <span className="font-bold text-lg">€/ha/year</span>, 
      dataIndex: 'price', 
      key: 'price',
      render: (price) => <span className="text-2xl font-bold text-primary">{price}</span>
    }
  ];

  const addOnData = [
    { key: '1', area: '< 1,000 ha', price: '10 €' },
    { key: '2', area: '1,000‑4,999', price: '8 €' },
    { key: '3', area: '5,000‑19,999', price: '6.5 €' },
    { key: '4', area: '20,000‑49,999', price: '5.5 €' },
    { key: '5', area: '≥ 50,000 ha', price: '4.5 €' }
  ];

  const items = [
    {
      key: '1',
      label: (
        <div className="flex items-center space-x-3 text-xl font-bold">
          <div className="p-2 bg-gradient-to-r from-primary to-green-600 rounded-lg text-white">
            <Zap size={24} />
          </div>
          <span>{t('pricingTable.basePlatform')}</span>
        </div>
      ),
      children: (
        <div className="bg-gradient-to-br from-green-50 to-blue-50 p-6 rounded-2xl">
          <Table 
            columns={basePlatformColumns} 
            dataSource={basePlatformData} 
            pagination={false}
            size="large"
            className="custom-table"
          />
          <div className="mt-6 p-4 bg-yellow-50 rounded-xl border-l-4 border-yellow-400">
            <p className="text-sm text-gray-700 font-medium">
              💡 <strong>{t('pricingTable.freeTrialNote')}</strong> {t('pricingTable.freeTrialCopy')}
            </p>
          </div>
        </div>
      )
    },
    {
      key: '2',
      label: (
        <div className="flex items-center space-x-3 text-xl font-bold">
          <div className="p-2 bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg text-white">
            <Crown size={24} />
          </div>
          <span>{t('pricingTable.advancedAddOn')}</span>
        </div>
      ),
      children: (
        <div className="bg-gradient-to-br from-purple-50 to-pink-50 p-6 rounded-2xl">
          <Table 
            columns={addOnColumns} 
            dataSource={addOnData} 
            pagination={false}
            size="large"
            className="custom-table"
          />
          <div className="mt-6 space-y-3">
            <h4 className="text-lg font-semibold text-gray-800 mb-4">Premium Features Included:</h4>
            <div className="grid grid-cols-2 gap-3">
              <Tag color="green" className="text-sm py-2 px-4 rounded-full">🌱 Virtual Tensiometer</Tag>
              <Tag color="blue" className="text-sm py-2 px-4 rounded-full">💧 Soil‑Moisture Probe</Tag>
              <Tag color="orange" className="text-sm py-2 px-4 rounded-full">🍃 Leaf N‑Index</Tag>
              <Tag color="red" className="text-sm py-2 px-4 rounded-full">🐛 Pest/Disease Early Warning</Tag>
            </div>
          </div>
        </div>
      )
    }
  ];

  const regionalPlans = [
    {
      cropsCount: 1,
      price: '3,500',
      descriptionKey: 'descOne',
      gradient: 'from-blue-500 to-cyan-500',
      popular: false
    },
    {
      cropsCount: 3,
      price: '9,000',
      descriptionKey: 'descThree',
      gradient: 'from-green-500 to-emerald-500',
      popular: true
    },
    {
      cropsCount: 9,
      price: '24,000',
      descriptionKey: 'descNine',
      gradient: 'from-purple-500 to-pink-500',
      popular: false
    }
  ];

  return (
    <div className="py-32 bg-gradient-to-b from-light via-white to-light relative overflow-hidden">
      {/* Background decorations */}
      <div className="absolute top-0 left-0 w-96 h-96 bg-gradient-to-br from-primary/10 to-green-500/10 rounded-full blur-3xl"></div>
      <div className="absolute bottom-0 right-0 w-80 h-80 bg-gradient-to-br from-blue-500/10 to-purple-500/10 rounded-full blur-3xl"></div>
      
      <div className="container mx-auto px-6 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-20"
        >
          <h2 className="text-5xl md:text-6xl font-black mb-6 bg-gradient-to-r from-dark via-primary to-dark bg-clip-text text-transparent">{t('pricingTable.title')}</h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">{t('pricingTable.subtitle')}</p>
        </motion.div>

        {/* Pricing Calculator */}
        <PricingCalculator />
        
        <div className="max-w-7xl mx-auto">
          
          
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            viewport={{ once: true }}
          >
            <h3 className="text-4xl font-bold text-center mb-12 text-dark">{t('pricingTable.regionalTitle')}</h3>
            
            <div className="grid md:grid-cols-3 gap-8 mb-12">
              {regionalPlans.map((plan, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.6 + index * 0.1 }}
                  viewport={{ once: true }}
                  className="relative"
                >
                  {plan.popular && (
                    <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 z-10">
                      <div className="bg-gradient-to-r from-yellow-400 to-orange-500 text-white px-4 py-1 rounded-full text-sm font-bold">
                        {t('pricingTable.mostPopular')}
                      </div>
                    </div>
                  )}
                  
                  <Card 
                    className={`text-center p-8 rounded-3xl shadow-xl hover:shadow-2xl transition-all duration-500 border-0 bg-gradient-to-br ${plan.gradient} text-white group hover:-translate-y-2 ${plan.popular ? 'ring-4 ring-yellow-400 ring-opacity-50' : ''}`}
                    bodyStyle={{ padding: 0 }}
                  >
                    <div className="mb-6">
                      <h4 className="text-2xl font-bold mb-2">{t('pricingTable.regional.cropsNationwide', { count: plan.cropsCount })}</h4>
                      <p className="text-white/80">{t(`pricingTable.regional.${plan.descriptionKey}`)}</p>
                    </div>
                    
                    <div className="mb-8">
                      <span className="text-5xl font-black">€{plan.price}</span>
                      <span className="text-white/80 ml-2">{t('common.perYear')}</span>
                  </div>
                    
                    <Button 
                      size="large" 
                      className="w-full bg-white/20 border-white/30 text-white hover:bg-white hover:text-gray-800 backdrop-blur-sm font-semibold py-6 h-auto rounded-xl group-hover:scale-105 transition-all duration-300"
                      onClick={() => { setQuoteHideArea(true); setQuoteOpen(true); }}
                    >
                      {t('pricingCalc.getQuote')}
                    </Button>
                  </Card>
                </motion.div>
              ))}
            </div>
            <div className="text-center mt-6 mb-12">
              <p className="text-sm text-gray-600">{t('pricingTable.coverage')}</p>
            </div>
            
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ delay: 1, duration: 0.8 }}
              viewport={{ once: true }}
              className="text-center"
            >
              <div
                className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-green-100 to-blue-100 rounded-2xl border border-green-200 cursor-pointer hover:shadow-sm"
                onClick={() => setDiscountInfoOpen(true)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setDiscountInfoOpen(true);
                  }
                }}
                aria-label="More information about the 10% discount when supplying field truth data"
              >
                <p className="text-lg font-semibold text-gray-700 m-0">💰 <span className="text-green-600">{t('pricingTable.discountPercent', { percent: 10 })}</span> {t('pricingTable.discountBanner')}</p>
                <Info
                  size={18}
                  className="text-gray-500 hover:text-primary cursor-pointer"
                  onClick={() => setDiscountInfoOpen(true)}
                  aria-label="More information about discount"
                />
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
      <QuoteModal open={quoteOpen} onClose={() => { setQuoteOpen(false); setQuoteHideArea(false); }} hideArea={quoteHideArea} />
      <Drawer
        title="Field Truth Data Discount"
        placement="right"
        onClose={() => setDiscountInfoOpen(false)}
        open={discountInfoOpen}
        width={420}
      >
        <div className="space-y-3 text-gray-700">
          <p>Details about the 10% discount will go here. We’ll replace this placeholder with your finalized copy.</p>
        </div>
      </Drawer>
    </div>
  );
};

export default PricingTable;
